import { useEffect, useState } from "react";

import { Checkbox, Divider, FormControl, InputLabel, ListItemText, MenuItem, Select } from "@mui/material";

import { useClusterDetailDashboardContext } from "../context";

interface WorkloadSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  autoSelectWorkloadsCount?: number;
}

const WorkloadSelector = ({ value = [], onChange, autoSelectWorkloadsCount = 10 }: WorkloadSelectorProps) => {
  const [touched, setTouched] = useState(false);

  const { dashboardWorkloads } = useClusterDetailDashboardContext();

  const workloadKeys = dashboardWorkloads.map((w) => w.id);
  const allValuesAreSelected = workloadKeys.every((v) => value.includes(v));

  const handleSelect = (workload: string) => {
    if (!value) return;
    const newSelectedValues = value.includes(workload) ? value.filter((v) => v !== workload) : [...value, workload];
    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    const newSelectedValues = allValuesAreSelected ? [] : workloadKeys;
    onChange(newSelectedValues);
  };

  useEffect(() => {
    if (autoSelectWorkloadsCount > 0 && dashboardWorkloads.length > 0 && !touched) {
      const workloadKeysToSelect = dashboardWorkloads
        .toSorted((a, b) => b.totalCost - a.totalCost)
        .slice(0, autoSelectWorkloadsCount)
        .map((w) => w.id);

      onChange(workloadKeysToSelect);
      setTouched(true);
    }
  }, [autoSelectWorkloadsCount, onChange, workloadKeys, touched, dashboardWorkloads]);

  const getWorkloadName = (id: string) => {
    const workload = dashboardWorkloads.find((w) => w.id === id);
    return workload?.name || id;
  };

  return (
    <FormControl disabled={!workloadKeys.length} variant="outlined" size="small">
      <InputLabel size="small">Workloads</InputLabel>
      <Select
        size="small"
        multiple
        value={value}
        label="Workloads"
        sx={{
          width: 200,
        }}
        renderValue={(selected) => selected.map(getWorkloadName).join(", ")}
      >
        <MenuItem onClick={handleSelectAll}>
          <Checkbox checked={allValuesAreSelected} size="small" />
          <ListItemText primary="Select All" />
        </MenuItem>
        <Divider />
        {workloadKeys.map((key) => (
          <MenuItem
            key={key}
            value={key}
            onClick={() => {
              handleSelect(key);
            }}
          >
            <Checkbox checked={value.includes(key)} size="small" />
            <ListItemText primary={getWorkloadName(key)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default WorkloadSelector;
